import { useEffect } from "react";
import { useTranslate } from "@/lib/i18n";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DriverSettlementCardView,
  DriverSettlementsOpenResponse,
} from "@/lib/api/dispatchTypes";
import { useApiQuery } from "@/shared/queries";
import { useMoney } from "@/shared/ui";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { isolate } from "./dispatchBoard";
import { dispatchQueryKeys } from "./useDispatchBoard";

/** T20's query key — the Delivery tab's settlement cards read the same one. */
export const settlementsOpenQueryKey = [...dispatchQueryKeys.all, "settlements", "open"] as const;

/** One bag a driver has just delivered, for the cashier's toast. */
export type DeliveredNotice = {
  assignmentId: string;
  driverName: string;
  sequence: number;
  method: "CASH" | "INSTAPAY";
  /** What the driver collected (cash) or what came by InstaPay; 0 for a prepaid bag. */
  amount: number;
};

/** assignmentId → the dispatch status last read, for the whole page session. */
export type DeliveredMemory = { seen: Map<string, string>; told: Set<string>; primed: boolean };

export function createDeliveredMemory(): DeliveredMemory {
  return { seen: new Map(), told: new Set(), primed: false };
}

/**
 * The bags whose assignment moved to DELIVERED since the previous read
 * (2026-10-20). Pure; updates `memory`:
 *
 *  - the FIRST read only records what it sees (opening the till says nothing);
 *  - a bag counts only when it was read before with another status — one that
 *    first appears already DELIVERED is not a transition anyone saw;
 *  - each assignment is told once (`told`), however often the data refetches.
 */
export function deliveredTransitions(
  memory: DeliveredMemory,
  cards: readonly DriverSettlementCardView[],
): DeliveredNotice[] {
  const notices: DeliveredNotice[] = [];
  for (const card of cards) {
    if (card.subject.kind !== "DRIVER") continue;
    for (const bag of card.bags) {
      if (!bag.assignmentId || !bag.dispatchStatus) continue;
      const before = memory.seen.get(bag.assignmentId);
      memory.seen.set(bag.assignmentId, bag.dispatchStatus);
      if (!memory.primed || bag.dispatchStatus !== "DELIVERED") continue;
      if (before === undefined || before === "DELIVERED" || memory.told.has(bag.assignmentId)) continue;
      memory.told.add(bag.assignmentId);
      const instapay = bag.doorMethod === "INSTAPAY";
      notices.push({
        assignmentId: bag.assignmentId,
        driverName: card.name,
        sequence: bag.orderNumber,
        method: instapay ? "INSTAPAY" : "CASH",
        amount: (instapay ? bag.instapayAmount : bag.cashCollected) ?? 0,
      });
    }
  }
  memory.primed = true;
  return notices;
}

/* One memory per page session (module scope): a bag delivered while the till
   was on a bill is still told when the floor comes back, and never twice. */
const sessionMemory = createDeliveredMemory();

/**
 * «سلّم ⟨driver⟩ الطلب رقم ⟨n⟩ · حصّل ⟨amount⟩» — a success toast for the
 * cashier when one of the restaurant's own drivers marks a bag DELIVERED
 * (2026-10-20). Read off the drivers' settlement cards (T20), which list every
 * bag on a driver's run with its dispatch status, door method and the money —
 * the same query the Delivery tab draws, refreshed by the realtime `floor`
 * signal (the backend bumps it on every assignment write). No new endpoint.
 *
 * Mounted by the floors' shared `useFloorDeliveryTab`, so the till and the
 * manager's floor both say it whichever tab is showing.
 */
export function useDeliveredToasts({
  enabled,
  memory = sessionMemory,
}: {
  enabled: boolean;
  memory?: DeliveredMemory;
}) {
  const t = useTranslate();
  const money = useMoney();
  const toast = usePrivateToast();
  const query = useApiQuery<DriverSettlementsOpenResponse>(
    settlementsOpenQueryKey,
    endpoints.restaurant.dispatchSettlementsOpen,
    { enabled, refetchOnWindowFocus: true },
  );
  const cards = enabled ? query.data?.cards : undefined;
  useEffect(() => {
    if (!cards) return;
    for (const notice of deliveredTransitions(memory, cards)) {
      const values = { name: isolate(notice.driverName), sequence: notice.sequence, amount: money(notice.amount) };
      toast.success(
        notice.amount <= 0
          ? t((m) => m.dispatch.deliveredToast, values)
          : notice.method === "INSTAPAY"
            ? t((m) => m.dispatch.deliveredToastInstapay, values)
            : t((m) => m.dispatch.deliveredToastCash, values),
      );
    }
    // `cards` is the trigger; the formatters are stable enough per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, memory]);
}
